import {
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Select from "react-select";
import { AuthContext } from "../../state/AuthContext";
import { ClientContext } from "../../state/ClientContext";
import NotificationAlert from "../NotificationAlert";
import PontosenseAdminLauncher from "../pontosense/PontosenseAdminLauncher";
import { SessionCountdownSlot } from "../sessionCountdownSlot";
import WebPushPermissionBanner, {
  WEB_PUSH_LOGIN_PROMPT_KEY,
} from "../WebPushPermissionBanner";
import { shouldShowWebPushBanner } from "../webPushBannerVisibility";
import useWebPushSubscription from "../../Hooks/useWebPushSubscription";
import "./Layout.css";

const Layout = ({
  children,
  className,
  containerClassName,
  showSideNav = true,
  isTouched: _isTouched = false,
  showNotificationAlert = false,
}) => {
  const {
    selectedClient,
    changeSelectedClient,
    clients,
    caregivers,
    selectedCaregiver,
    changeSelectedCaregiver,
  } = useContext(ClientContext);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [showWebPushBanner, setShowWebPushBanner] = useState(false);

  const {
    currentUser,
    logout,
  } = useContext(AuthContext);
  const isStaffUser =
    currentUser &&
    (currentUser.isAdmin ||
      currentUser.isSuperAdmin ||
      currentUser.isCaregiver);
  const shouldShowNotificationBell = showNotificationAlert || isStaffUser;

  const {
    enableWebPush,
    supportsWebPush,
    notificationPermission,
    isSubscribed,
    subscriptionChecked,
  } = useWebPushSubscription({
    enabled: Boolean(shouldShowNotificationBell && currentUser),
  });
  const navigate = useNavigate();
  const location = useLocation();


  const [clientOptions, setClientOptions] = useState([]);
  const [caregiverOptions, setCaregiverOptions] = useState([]);

  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(80);

  useLayoutEffect(() => {
    const updateHeaderHeight = () => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.getBoundingClientRect().height);
      }
    };

    updateHeaderHeight();
    window.addEventListener("resize", updateHeaderHeight);
    return () => window.removeEventListener("resize", updateHeaderHeight);
  }, []);

  useEffect(() => {
    setShowWebPushBanner(
      shouldShowWebPushBanner({
        staffBellEnabled: shouldShowNotificationBell,
        supportsWebPush,
        hasUser: Boolean(currentUser),
        notificationPermission,
        isSubscribed,
        subscriptionChecked,
      }),
    );
  }, [
    shouldShowNotificationBell,
    supportsWebPush,
    currentUser,
    notificationPermission,
    isSubscribed,
    subscriptionChecked,
  ]);

  const toggleDropdown = () => {
    // Copilot follow-up (PR #216): use the functional updater so rapid
    // successive clicks toggle off the latest value, not a stale closure.
    setIsDropdownVisible((visible) => !visible);
    void enableWebPush().then((ok) => {
      if (ok) {
        setShowWebPushBanner(false);
        try {
          sessionStorage.removeItem(WEB_PUSH_LOGIN_PROMPT_KEY);
        } catch (_err) {
          // ignore
        }
      }
    });
  };

  const onInputChangeClient = (newValue) => {
    const clientId = newValue.value;

    changeSelectedCaregiver(null);
    changeSelectedClient(null);
    if (clientId === "create") {
      navigate("/add-client");
      return;
    }
    changeSelectedClient(newValue.id);

    navigate(`/client_profile/${clientId}`);
  };

  const onInputChangeCaregiver = (newValue) => {
    const caregiverId = newValue.value;

    changeSelectedClient(null);
    changeSelectedCaregiver(null);
    if (caregiverId === "create") {
      navigate("/add-admin");
      return;
    }
    if (caregiverId === "create-caregiver") {
      navigate("/add-caregiver");
      return;
    }
    changeSelectedCaregiver(caregiverId);
    navigate(`/caregiver_profile/${caregiverId}`);
  };

  useEffect(() => {
    if (!clients || !currentUser) return;

    setClientOptions([
      ...(currentUser?.isSuperAdmin || currentUser?.isAdmin
        ? [{ value: "create", label: "--- Add new client ---" }]
        : []),
      ...clients,
    ]);
  }, [clients, currentUser]);

  useEffect(() => {
    if (
      (!location.pathname.includes("select-client-homepage") &&
        location.pathname.includes("/calendar")) ||
      location.pathname.includes("/contacts") ||
      location.pathname.includes("/call") ||
      location.pathname.includes("/client_profle")
    ) {
      if (location.pathname.includes("/contacts")) {
        navigate(`/contacts/${selectedClient.id}`);
      } else if (location.pathname.includes("/call")) {
        navigate(`/call/${selectedClient.id}`);
      } else if (location.pathname.includes("/calendar")) {
        navigate(`/calendar/${selectedClient.id}`);
      }
    }
  }, [selectedClient, location.pathname]);

  useEffect(() => {
    if (!caregivers || !currentUser) return;

    const hasAdminAccess = currentUser?.isSuperAdmin || currentUser?.isAdmin;
    setCaregiverOptions([
      ...(currentUser?.isSuperAdmin
        ? [{ value: "create", label: "--- Add new admin ---" }]
        : []),
      ...(hasAdminAccess
        ? [{ value: "create-caregiver", label: "--- Add new caregiver ---" }]
        : []),
      ...(hasAdminAccess
        ? caregivers
        : caregivers.filter((cg) => cg.id === currentUser.id)),
    ]);
  }, [caregivers, currentUser]);

  const handleHomepageClick = () => {
    if (
      (window.location.pathname.includes("/add") ||
        window.location.pathname.match("_profile")) &&
      _isTouched
    ) {
      return;
    }

    changeSelectedCaregiver(null);
    changeSelectedClient(null);
    navigate("/select-client-homepage");
  };

  const handleClientScheduleClick = () => {
    if (selectedClient) {
      navigate(`/calendar/${selectedClient.id}`);
    }
  };

  const handleCallListsCLick = () => {
    if (selectedClient) {
      navigate(`/contacts/${selectedClient.id}`);
    }
  };

  const handleCallClick = () => {
    if (selectedClient) {
      navigate(`/call/${selectedClient.id}`);
    }
  };

  const handleClientInfoClick = () => {
    navigate(`/client_profile/${selectedClient.id}`);
  };

  return (
    <div className="outer-container">
      <header ref={headerRef} className="layout-header">
        <button className="logo-button" onClick={handleHomepageClick}>
          <img
            src="/images/Compass-Care_White.png"
            alt="Compass Care by Gray Matters Alliance"
            style={{ marginLeft: "10px", width: "200px" }}
          />
        </button>
        <div
          className="sch-client-selector"
          style={{ width: "22%", paddingRight: "20px" }}
        >
          <Select
            defaultValue={1}
            name="clients"
            onChange={onInputChangeClient}
            options={clientOptions}
            value={selectedClient}
            className="basic-single-select"
            classNamePrefix="select"
            placeholder={
              selectedClient ? selectedClient.name : "Search Clients"
            }
          />
        </div>
        <div style={{ width: "25%", marginRight: "auto" }}>
          <Select
            defaultValue={1}
            name="caregivers"
            onChange={onInputChangeCaregiver}
            options={caregiverOptions}
            value={selectedCaregiver}
            className="basic-single-select"
            classNamePrefix="select"
            placeholder={
              selectedCaregiver ? selectedCaregiver.name : "Search Caregivers"
            }
          />
        </div>
        {!currentUser?.isSuperAdmin && !currentUser?.isAdmin && (
          <div
            style={{ color: "#fff", fontSize: "1.2rem", marginRight: "10px" }}
          >
            <p style={{ margin: 0 }}>Need help? Gray Matters Alliance</p>
            <p style={{ margin: 0 }}>
              is here for you. Call{" "}
              <a href="tel:+13142662678" style={{ textDecoration: "none" }}>
                314-266-2678 ext 805
              </a>
            </p>
          </div>
        )}
        {/* GMA-1909: reserved space for the single countdown, ahead of the
            controls it used to float on top of. */}
        <SessionCountdownSlot />
        <PontosenseAdminLauncher currentUser={currentUser} />
        <button
          className="btn-primary-lg"
          onClick={(ev) => {
            ev.preventDefault();
            logout();
          }}
        >
          Log Out
        </button>
        {shouldShowNotificationBell && (
          // #254: the bell toggle must be keyboard- and SR-operable (WCAG 2.1.1
          // / 4.1.2). It stays a div with role="button" rather than a real
          // <button> because NotificationAlert renders nested role="button"
          // dismiss rows in its dropdown — a <button> wrapping interactive
          // children would be invalid markup.
          <div
            className="notification-icon"
            role="button"
            tabIndex={0}
            aria-haspopup="true"
            aria-expanded={isDropdownVisible}
            aria-label="Notifications"
            onClick={toggleDropdown}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                toggleDropdown();
              }
            }}
          >
            <NotificationAlert
              isDropdownVisible={isDropdownVisible}
              currentUser={currentUser}
            />
          </div>
        )}
      </header>
      <div
        style={{
          marginTop: `${headerHeight - 2}px`,
          // #724: the sticky side nav (Layout.css .sidebar-nav) offsets
          // itself by the measured header so it never slides under it.
          "--layout-header-height": `${headerHeight}px`,
          // GMA-1318 (Copilot #201): the banner wrapper must be a flex column
          // that grows, so the existing `.main-content-container { flex-grow: 1 }`
          // rule still fills the remaining viewport height (scrolling/left-nav).
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          minHeight: 0,
        }}
      >
        <WebPushPermissionBanner
          visible={showWebPushBanner && supportsWebPush}
          isSubscribed={isSubscribed}
          onEnable={enableWebPush}
          onDismiss={() => {
            // sessionStorage writes (remove login-prompt key + set dismissed key)
            // are owned by WebPushPermissionBanner's handleDismiss; don't duplicate here.
            setShowWebPushBanner(false);
          }}
        />
      <div
        className={`main-content-container ${containerClassName ?? ""}`}
        style={{ marginTop: 0 }}
      >
        {showSideNav && (
          <div className="left-nav">
            <ul className="sidebar-nav">
              <li onClick={handleClientInfoClick}>Client info</li>
              <li onClick={handleClientScheduleClick}>Schedule</li>
              <li onClick={handleCallClick}>Video call</li>
              <li onClick={handleCallListsCLick}>Contacts</li>
            </ul>
          </div>
        )}
        <div className={`main-content ${className ?? ""}`}>{children}</div>
      </div>
      </div>
    </div>
  );
};

export default Layout;
